import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Trophy, MapPin, Calendar, Briefcase, TrendingUp, Award, Building2, Loader2, AlertCircle,
  Crown, Star, Medal,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  LineChart, Line,
} from "recharts";
import { API } from "@/context/AuthContext";

const fmtEur = (n, mm = true) => {
  if (!n) return "0";
  if (mm) {
    const m = n / 1_000_000;
    return m >= 10 ? `${m.toFixed(0)}` : `${m.toFixed(1)}`;
  }
  return Math.round(n).toLocaleString("es-ES");
};

const COLOR_BRAND = "#007934";
const COLOR_ACCENT = "#FFC107";
const SECTOR_COLORS = ["#007934", "#0F9D58", "#34C759", "#5AC8FA", "#FFC107", "#FF9500", "#FF3B30", "#AF52DE"];

const SLIDES = ["overview", "carousel", "map", "sectors", "hall_of_fame", "teams"];

export default function WowDisplay() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const carouselRef = useRef(0);

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [slideIdx, setSlideIdx] = useState(0);
  const [carouselIdx, setCarouselIdx] = useState(0);

  // Fetch + auto-refresh cada 5 min
  const fetchData = useCallback(async () => {
    if (!token) { setError("Token requerido. Añade `?token=XXX` en la URL."); return; }
    try {
      const { data } = await axios.get(`${API}/public/wow/data?token=${encodeURIComponent(token)}`);
      setData(data);
      setError("");
    } catch (e) {
      setError(e.response?.status === 401 ? "Token inválido." : `Error: ${e.message}`);
    }
  }, [token]);

  useEffect(() => { fetchData(); const t = setInterval(fetchData, 5 * 60 * 1000); return () => clearInterval(t); }, [fetchData]);

  // Rotación entre slides
  const rotateSec = data?.rotation_seconds || 18;
  useEffect(() => {
    if (!data) return;
    const t = setInterval(() => setSlideIdx(i => (i + 1) % SLIDES.length), rotateSec * 1000);
    return () => clearInterval(t);
  }, [data, rotateSec]);

  // Carrusel interno de adjudicaciones (rotación más rápida)
  useEffect(() => {
    if (!data?.top_recent?.length) return;
    const t = setInterval(() => {
      carouselRef.current = (carouselRef.current + 1) % data.top_recent.length;
      setCarouselIdx(carouselRef.current);
    }, 5500);
    return () => clearInterval(t);
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-3 text-red-400"/>
          <p className="text-xl">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <Loader2 size={48} className="animate-spin text-[#34C759]"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001f0e] via-[#003d1e] to-[#005028] text-white overflow-hidden relative font-['Outfit']" data-testid="wow-display">
      {/* Background noise / pattern */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }}/>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-12 pt-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center">
            <Trophy size={26} className="text-[#007934]"/>
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tight">{data.title}</h1>
            <p className="text-emerald-200/70 text-sm uppercase tracking-widest mt-1">Sistema de adjudicaciones · {data.year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-emerald-200/70 text-xs uppercase tracking-widest">Última actualización</p>
          <p className="text-sm font-mono text-emerald-100/90">{new Date(data.generated_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
        </div>
      </header>

      {/* Slide indicator */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {SLIDES.map((s, i) => (
          <div key={s} className={`h-1.5 rounded-full transition-all duration-500 ${i === slideIdx ? "w-12 bg-[#34C759]" : "w-2 bg-white/20"}`}/>
        ))}
      </div>

      {/* Slides */}
      <main className="relative z-10 px-12 py-8">
        {slideIdx === 0 && <SlideOverview data={data}/>}
        {slideIdx === 1 && <SlideCarousel data={data} idx={carouselIdx}/>}
        {slideIdx === 2 && <SlideMap data={data}/>}
        {slideIdx === 3 && <SlideSectors data={data}/>}
        {slideIdx === 4 && <SlideHallOfFame data={data}/>}
        {slideIdx === 5 && <SlideTeams data={data}/>}
      </main>

      <footer className="absolute bottom-4 left-12 right-12 flex justify-between text-xs text-emerald-200/40 tracking-wider z-10">
        <span>VALORIZA SERVICIOS MEDIOAMBIENTALES</span>
        <span>EXCELENCIA · CONFIANZA · LIDERAZGO</span>
      </footer>

      {/* CSS animations */}
      <style>{`
        @keyframes wowFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wowZoomIn {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes wowSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wow-fade-in  { animation: wowFadeIn  500ms ease-out both; }
        .wow-zoom-in  { animation: wowZoomIn  600ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .wow-slide-up { animation: wowSlideUp 550ms ease-out both; }
      `}</style>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SLIDES
// ════════════════════════════════════════════════════════════════════════════
function SlideOverview({ data }) {
  const k = data.kpis;
  return (
    <div className="space-y-8" data-testid="wow-slide-overview">
      <div className="grid grid-cols-4 gap-6">
        <KpiBig label={`Adjudicaciones en ${data.year}`} value={k.this_year_count} suffix="contratos" icon={Trophy}/>
        <KpiBig label="Cartera adjudicada" value={fmtEur(k.this_year_importe)} suffix="MM€" highlight icon={TrendingUp}/>
        <KpiBig label="Contratos firmados" value={k.firmados_count} suffix={`/ ${k.total_count}`} icon={Award}/>
        <KpiBig label="Facturación anual estimada" value={fmtEur(k.total_anual_eur)} suffix="MM€/año" icon={Calendar}/>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-8">
        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10">
          <p className="text-sm text-emerald-200/70 uppercase tracking-widest mb-3">Evolución mensual</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.timeseries.map(t => ({ ...t, importeMM: t.importe / 1_000_000 }))}>
              <XAxis dataKey="month" stroke="#a7f3d0" fontSize={11}/>
              <YAxis yAxisId="left" stroke="#a7f3d0" fontSize={11} label={{ value: "MM€", angle: -90, fill: "#a7f3d0", fontSize: 10 }}/>
              <YAxis yAxisId="right" orientation="right" stroke="#fcd34d" fontSize={11} label={{ value: "#", angle: 90, fill: "#fcd34d", fontSize: 10 }}/>
              <Tooltip contentStyle={{ background: "#001f0e", border: "1px solid #34C759", color: "#fff" }}/>
              <Line yAxisId="left" type="monotone" dataKey="importeMM" stroke={COLOR_ACCENT} strokeWidth={3} dot={{ r: 4 }} name="Cartera MM€"/>
              <Line yAxisId="right" type="monotone" dataKey="count" stroke="#34C759" strokeWidth={2} dot={{ r: 3 }} name="#"/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10">
          <p className="text-sm text-emerald-200/70 uppercase tracking-widest mb-3">Distribución por sector</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={data.sectors.map(s => ({ name: s.sector, value: s.importe }))}
                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}
                label={(e) => `${e.name} · ${fmtEur(e.value)}MM`}
                labelLine={false}>
                {data.sectors.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{ background: "#001f0e", border: "1px solid #34C759", color: "#fff" }} formatter={(v) => `${fmtEur(v)} MM€`}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function SlideCarousel({ data, idx }) {
  const items = data.top_recent || [];
  if (items.length === 0) return <p className="text-center py-32 text-emerald-200/50">Sin adjudicaciones recientes</p>;
  const item = items[idx % items.length];
  return (
    <div className="grid grid-cols-3 gap-8 items-center" data-testid="wow-slide-carousel">
      <div className="col-span-2 space-y-4">
        <p className="text-sm text-[#FFC107] uppercase tracking-widest font-bold">Adjudicación destacada {idx + 1} / {items.length}</p>
        <h2 className="text-6xl font-black leading-tight">{item.municipality || "—"}</h2>
        <p className="text-2xl text-emerald-100/90 leading-snug">{item.title}</p>
        <div className="flex flex-wrap items-center gap-3 pt-4">
          {item.ccaa && <Pill icon={MapPin}>{item.ccaa}</Pill>}
          {item.sector && <Pill icon={Briefcase}>{item.sector}</Pill>}
          {item.duracion_meses && <Pill icon={Calendar}>{item.duracion_meses} meses</Pill>}
          {item.fase && <Pill highlight>{item.fase}</Pill>}
        </div>
        {item.responsible && <p className="text-emerald-200/60 text-sm pt-2">Responsable: <span className="font-semibold text-emerald-100">{item.responsible}</span></p>}
      </div>
      <div className="bg-gradient-to-br from-[#FFC107] to-[#FF9500] rounded-3xl p-8 text-[#001f0e]">
        <p className="text-xs uppercase tracking-widest font-bold opacity-70">Importe total</p>
        <p className="text-7xl font-black leading-none mt-2">{fmtEur(item.importe)}</p>
        <p className="text-2xl font-bold mt-1">MM€</p>
        {item.anual > 0 && (
          <p className="text-sm mt-4 opacity-80">Facturación anual: <span className="font-bold">{fmtEur(item.anual)}</span> MM€/año</p>
        )}
      </div>

      {/* Mini next previews */}
      <div className="col-span-3 flex gap-3 overflow-hidden mt-6">
        {items.slice(0, 8).map((it, i) => (
          <div key={it.id} className={`flex-1 min-w-0 rounded-xl p-3 transition-all ${i === (idx % items.length) ? "bg-[#FFC107]/20 border border-[#FFC107]/50" : "bg-white/5 border border-white/10"}`}>
            <p className="text-[10px] text-emerald-200/60 uppercase tracking-wider truncate">{it.ccaa || "—"}</p>
            <p className="text-sm font-bold truncate">{it.municipality}</p>
            <p className="text-xs text-[#FFC107] font-mono">{fmtEur(it.importe)} MM€</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideMap({ data }) {
  const total = data.ccaa.reduce((a, c) => a + c.importe, 0);
  return (
    <div className="space-y-6" data-testid="wow-slide-map">
      <h2 className="text-3xl font-black">Presencia nacional · {data.ccaa.length} provincias / CCAA</h2>
      <div className="grid grid-cols-3 gap-4">
        {data.ccaa.slice(0, 15).map((c, i) => {
          const pct = total > 0 ? (c.importe / total) * 100 : 0;
          return (
            <div key={c.ccaa} className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold uppercase tracking-wide text-emerald-100">{c.ccaa}</p>
                <span className="text-xs bg-[#FFC107]/20 text-[#FFC107] px-2 py-0.5 rounded-md font-bold">#{i + 1}</span>
              </div>
              <p className="text-3xl font-black text-[#34C759]">{fmtEur(c.importe)}<span className="text-base font-semibold ml-1 text-emerald-200/70">MM€</span></p>
              <div className="flex items-center justify-between mt-2 text-xs text-emerald-200/60">
                <span>{c.count} {c.count === 1 ? "contrato" : "contratos"}</span>
                <span>{c.ciudades} {c.ciudades === 1 ? "ciudad" : "ciudades"}</span>
              </div>
              <div className="h-1 mt-3 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#34C759] to-[#FFC107]" style={{ width: `${pct}%` }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlideSectors({ data }) {
  const total = data.sectors.reduce((a, c) => a + c.importe, 0);
  return (
    <div className="space-y-6" data-testid="wow-slide-sectors">
      <h2 className="text-3xl font-black">Líderes del sector medioambiental</h2>
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={data.sectors.map(s => ({ ...s, importeMM: s.importe / 1_000_000 }))} layout="vertical" margin={{ left: 80 }}>
          <XAxis type="number" stroke="#a7f3d0" fontSize={12}/>
          <YAxis dataKey="sector" type="category" stroke="#fff" fontSize={14} width={160}/>
          <Tooltip contentStyle={{ background: "#001f0e", border: "1px solid #34C759", color: "#fff" }} formatter={(v) => `${v.toFixed(1)} MM€`}/>
          <Bar dataKey="importeMM" radius={[0, 8, 8, 0]} label={{ position: "right", fill: "#fff", fontSize: 14, fontWeight: 700, formatter: (v) => `${v.toFixed(1)} MM€` }}>
            {data.sectors.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]}/>)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-4 gap-3">
        {data.sectors.slice(0, 4).map((s, i) => (
          <div key={s.sector} className="bg-white/5 rounded-xl p-3 border border-white/10">
            <div className="w-3 h-3 rounded-full mb-2" style={{ background: SECTOR_COLORS[i % SECTOR_COLORS.length] }}/>
            <p className="text-sm font-bold">{s.sector}</p>
            <p className="text-2xl font-black text-[#FFC107]">{fmtEur(s.importe)} MM€</p>
            <p className="text-xs text-emerald-200/60">{s.count} contratos · {total > 0 ? ((s.importe / total) * 100).toFixed(0) : 0}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideHallOfFame({ data }) {
  const fame = data.hall_of_fame || [];
  if (fame.length === 0) {
    return (
      <div className="text-center py-32 text-emerald-200/50" data-testid="wow-slide-hof">
        <Crown size={60} className="mx-auto mb-4 opacity-40"/>
        <p className="text-xl">Aún no hay datos suficientes de responsables comerciales</p>
      </div>
    );
  }

  // Paleta de degradados para avatares (deterministas por nombre)
  const palettes = [
    "from-amber-400 to-yellow-600",
    "from-emerald-400 to-green-700",
    "from-sky-400 to-blue-700",
    "from-rose-400 to-pink-700",
    "from-purple-400 to-fuchsia-700",
  ];
  const pickPalette = (name) => palettes[Math.abs(name.split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % palettes.length];

  // Podio: orden visual = 2º, 1º, 3º
  const podium = fame.slice(0, 3);
  const others = fame.slice(3);
  const arr = podium.length === 3 ? [podium[1], podium[0], podium[2]] : podium;

  return (
    <div className="space-y-8" data-testid="wow-slide-hof">
      <div className="flex items-center gap-3 wow-fade-in" style={{ animationDelay: "0ms" }}>
        <Crown size={32} className="text-[#FFC107]"/>
        <h2 className="text-4xl font-black">Hall of Fame · Top responsables comerciales</h2>
      </div>

      {/* Podio */}
      <div className="grid grid-cols-3 gap-6 items-end">
        {arr.map((p, i) => {
          const pos = podium.length === 3 ? (i === 0 ? 2 : i === 1 ? 1 : 3) : (i + 1);
          const heightCls = pos === 1 ? "h-72" : pos === 2 ? "h-56" : "h-48";
          const ringCls = pos === 1 ? "ring-4 ring-[#FFC107]/60" : "ring-2 ring-white/15";
          const palette = pickPalette(p.name);
          const Icon = pos === 1 ? Crown : pos === 2 ? Medal : Star;
          // Stagger: 3º entra primero (más rápido), luego 2º, luego 1º (climax)
          const delay = (4 - pos) * 250;
          return (
            <div
              key={p.name}
              className={`relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-6 flex flex-col items-center justify-end wow-zoom-in ${heightCls}`}
              style={{ animationDelay: `${delay}ms` }}>
              <span className={`absolute -top-4 right-4 text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full ${
                pos === 1 ? "bg-[#FFC107] text-[#001f0e]" :
                pos === 2 ? "bg-emerald-300 text-[#001f0e]" :
                "bg-orange-300 text-[#001f0e]"
              }`}>
                {pos === 1 ? "🥇 1º" : pos === 2 ? "🥈 2º" : "🥉 3º"}
              </span>
              {p.avatar ? (
                <img src={p.avatar} alt={p.name}
                     className={`w-28 h-28 rounded-full object-cover mb-4 ${ringCls} shadow-2xl`} />
              ) : (
                <div className={`w-28 h-28 rounded-full bg-gradient-to-br ${palette} flex items-center justify-center mb-4 ${ringCls} shadow-2xl`}>
                  <span className="text-4xl font-black text-white drop-shadow-lg">{p.initials}</span>
                </div>
              )}
              <p className="text-xl font-black text-center leading-tight">{p.name}</p>
              <div className="mt-3 flex flex-col items-center gap-1">
                <p className="text-3xl font-black text-[#FFC107]">{(p.importe / 1_000_000).toFixed(1)} <span className="text-lg">MM€</span></p>
                <div className="flex items-center gap-3 text-xs text-emerald-200/70 mt-1">
                  <span><Icon size={11} className="inline mr-1"/>{p.count} contratos</span>
                  <span>·</span>
                  <span><Building2 size={11} className="inline mr-1"/>{p.ciudades} ciudades</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Resto del top 5 */}
      {others.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {others.map((p, i) => {
            const palette = pickPalette(p.name);
            return (
              <div
                key={p.name}
                className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-4 flex items-center gap-4 wow-fade-in"
                style={{ animationDelay: `${900 + i * 150}ms` }}>
                {p.avatar ? (
                  <img src={p.avatar} alt={p.name}
                       className="w-14 h-14 rounded-full object-cover shadow-lg flex-shrink-0"/>
                ) : (
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${palette} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <span className="text-xl font-black text-white">{p.initials}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold truncate">{p.name}</p>
                  <p className="text-xs text-emerald-200/60">#{i + 4} del ranking · {p.count} contratos · {p.ciudades} ciudades</p>
                </div>
                <p className="text-xl font-black text-[#FFC107]">{(p.importe / 1_000_000).toFixed(1)} MM€</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SlideTeams({ data }) {
  const teams = data.teams || [];
  if (teams.length === 0) {
    return (
      <div className="text-center py-32 text-emerald-200/50" data-testid="wow-slide-teams">
        <Building2 size={60} className="mx-auto mb-4 opacity-40"/>
        <p className="text-xl">Sin datos de Dirección Regional</p>
      </div>
    );
  }
  const total = teams.reduce((a, t) => a + t.importe, 0);
  const palettes = [
    "from-[#FFC107] to-[#FF9500]",
    "from-emerald-400 to-[#007934]",
    "from-sky-400 to-blue-700",
    "from-purple-400 to-fuchsia-700",
    "from-rose-400 to-red-700",
    "from-orange-400 to-amber-700",
  ];

  return (
    <div className="space-y-6" data-testid="wow-slide-teams">
      <div className="flex items-center gap-3 wow-fade-in">
        <Building2 size={32} className="text-[#34C759]"/>
        <h2 className="text-4xl font-black">Liderazgo por Dirección Regional</h2>
      </div>

      <div className="grid grid-cols-3 gap-5">
        {teams.map((t, i) => {
          const pct = total > 0 ? (t.importe / total) * 100 : 0;
          const palette = palettes[i % palettes.length];
          return (
            <div
              key={t.team}
              className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-6 overflow-hidden wow-slide-up"
              style={{ animationDelay: `${i * 150}ms` }}>
              {/* Decoration */}
              <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br ${palette} opacity-20 blur-xl`}/>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-md bg-white/10 text-emerald-100">
                    #{i + 1} del ranking
                  </span>
                  {i === 0 && <Crown size={22} className="text-[#FFC107]"/>}
                </div>
                <p className="text-xl font-black leading-tight mb-1">{t.team}</p>
                <p className={`text-5xl font-black bg-gradient-to-br ${palette} bg-clip-text text-transparent mt-3`}>
                  {(t.importe / 1_000_000).toFixed(1)}
                  <span className="text-xl ml-1">MM€</span>
                </p>
                <div className="flex items-center justify-between mt-4 text-xs text-emerald-200/70">
                  <span>{t.count} contratos</span>
                  <span>{t.ciudades} clientes</span>
                </div>
                <div className="h-1.5 mt-4 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${palette}`} style={{ width: `${pct}%` }}/>
                </div>
                <p className="text-right text-[10px] text-emerald-200/50 mt-1">{pct.toFixed(0)}% del total</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KpiBig({ label, value, suffix, highlight, icon: Icon }) {
  return (
    <div className={`rounded-3xl p-6 border ${highlight ? "bg-gradient-to-br from-[#FFC107]/20 to-[#FF9500]/10 border-[#FFC107]/40" : "bg-white/5 border-white/10"} backdrop-blur-md`}>
      {Icon && <Icon size={20} className={highlight ? "text-[#FFC107]" : "text-emerald-300"}/>}
      <p className="text-xs uppercase tracking-widest mt-3 mb-1 text-emerald-200/70">{label}</p>
      <p className={`text-6xl font-black tracking-tight leading-none ${highlight ? "text-[#FFC107]" : "text-white"}`}>{value}</p>
      <p className="text-sm font-bold mt-2 text-emerald-100/80">{suffix}</p>
    </div>
  );
}

function Pill({ icon: Icon, highlight, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${highlight ? "bg-[#FFC107]/20 text-[#FFC107] border border-[#FFC107]/40" : "bg-white/10 text-white border border-white/15"}`}>
      {Icon && <Icon size={14}/>}
      {children}
    </span>
  );
}
